<template>
  <DsCard variant="outlined" padding="md" class="w-full">
    <h4 class="text-[1rem] leading-[1.5] text-primary font-inter font-semibold mb-4">
      {{ cardTitle }}
    </h4>
    <div class="w-full flex flex-col gap-4">
      <Vueform
        :key="formKey"
        class="w-full"
        :float-placeholders="false"
        :display-errors="false"
        :force-labels="false"
        :display-messages="false"
        v-model="formData"
        ref="formRef"
      >
        <TextElement
          class="locked-field"
          name="cgName"
          v-model="formData.cgName"
          :default="formData.cgName"
          label="Connect Group"
          placeholder="None"
          :rules="['required', invalidGroup]"
          :messages="{
            required: 'Required',
            invalidGroup: 'Enter a valid Connect Group',
          }"
          :disabled="fieldDisable"
        />
      </Vueform>
      <div v-if="isRequested" class="w-full flex gap-[8px]">
        <DsButton variant="borderless" size="48" class="flex-1" @click="onRequestCancel"
          >Cancel</DsButton
        >
        <DsButton
          variant="bordered"
          shape="pill"
          size="48"
          class="flex-1"
          :loading="loading"
          :disabled="disabledSubmit"
          @click="handleSubmit"
          >Submit</DsButton
        >
      </div>
      <DsButton v-else variant="bordered" shape="pill" size="48" class="w-full" @click="onRequest">
        {{ requestText }}
        <DsIcon name="edit" size="sm" class="ms-1" />
      </DsButton>
    </div>
  </DsCard>
</template>
<script>
import { VueForm, Validator } from '@vueform/vueform';
import { mapState } from 'vuex';
import axios from 'axios';
import { ERROR_GROUP } from '@/constants/validation/error-messages';
import { resolveCgId } from '@/utils/cg-status';
import {
  Card as DsCard,
  Button as DsButton,
  Icon as DsIcon,
} from '@team-hogc/hogc-one-design-system';

export default {
  components: {
    VueForm,
    DsCard,
    DsButton,
    DsIcon,
  },
  props: {
    payload: {
      type: Object,
      default: {},
    },
    cgData: {
      type: Object,
      default: {},
    },
    showError: {
      type: Object,
      default: {},
    },
  },
  computed: {
    ...mapState('user', ['token']),
  },
  emits: ['hideFooter'],
  mounted() {
    if (Object.keys(this.payload).length > 0) {
      this.updatedPayload = JSON.parse(JSON.stringify(this.payload));
      this.formData.cgName = this.payload.cgName || '';
      if (this.payload.cgUpdateRequestFormId) {
        this.cardTitle = 'Thanks! We’ve received your request and will be in touch soon ✅';
      } else {
        this.cardTitle = 'Request for an update if these fields are incorrect.';
      }
    }
  },
  data() {
    return {
      requestText: 'Request for an update',
      isRequested: false,
      fieldDisable: true,
      cardTitle: 'Request for an update if these fields are incorrect.',
      disabledSubmit: false,
      updatedPayload: {},
      formKey: 0,
      loading: false,
      formErrors: {
        cgName: false,
      },
      formData: {
        cgName: '',
      },
    };
  },
  methods: {
    onRequest() {
      this.isRequested = !this.isRequested;
      this.fieldDisable = !this.fieldDisable;
      if (this.fieldDisable) {
        this.cardTitle = 'Request for an update if these fields are incorrect.';
      } else {
        this.cardTitle = 'Which connect group (CG) do you belong to?';
      }
    },
    async handleSubmit() {
      // Validate form before submitting
      if (this.$refs.formRef) {
        await this.$refs.formRef.validate();
        const errors = this.$refs.formRef.el$('cgName')?.errors || [];
        if (errors.length > 0) {
          this.formErrors.cgName = true;
          this.disabledSubmit = true;
          return;
        }
      }
      this.formErrors.cgName = false;
      this.disabledSubmit = false;
      try {
        await this.onRequestSubmit(); // Call the function if no errors
      } catch (e) {
        console.error('CG update request error:', e);
        this.$store.dispatch('toast/error', 'Failed to submit request. Please try again.');
      }
    },
    async onRequestSubmit() {
      // Normalize CG name: remove whitespace and convert to uppercase
      const normalizedCgName = this.formData.cgName.toUpperCase().replace(/\s+/g, '');
      // Resolve the id through the shared lookup, which normalises the directory's
      // keys too. Indexing the map with the stripped name never matched a spaced
      // key ("TEENS 1"), so every multi-word CG reached the request with an
      // undefined id and failed on `.toString()`.
      const cgId = resolveCgId(this.formData.cgName, this.cgData);
      if (cgId === undefined) {
        throw new Error(`CG "${normalizedCgName}" is not in the directory`);
      }
      this.payload.cgName = normalizedCgName;
      this.payload.cgId = cgId;
      this.updatedPayload.cgName = normalizedCgName;
      this.updatedPayload.cgId = cgId;

      this.loading = true;
      this.cardTitle = 'Submitting Request...';

      try {
        await axios.post(
          process.env.VUE_APP_API_URL + '/v2/profile/cg-update-request',
          {
            cgId: String(cgId),
            cgName: normalizedCgName,
          },
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          },
        );
      } catch (e) {
        this.loading = false;
        this.cardTitle = 'Which connect group (CG) do you belong to?';
        throw e; // Re-throw for handleSubmit to catch
      }

      this.loading = false;
      this.requestText = 'Edit request';
      this.isRequested = !this.isRequested;
      this.fieldDisable = !this.fieldDisable;
      this.payload.cgUpdateRequestFormId = true;
      this.cardTitle = 'Thanks! We’ve received your request and will be in touch soon ✅';
    },
    onRequestCancel() {
      this.requestText = 'Request for an update';
      this.isRequested = false;
      this.fieldDisable = true;
      if (this.payload.cgUpdateRequestFormId) {
        this.cardTitle = 'Thanks! We’ve received your request and will be in touch soon ✅';
      } else {
        this.cardTitle = 'Request for an update if these fields are incorrect.';
      }
      this.updatedPayload = JSON.parse(JSON.stringify(this.payload));
      this.formData.cgName = this.payload.cgName || '';
      this.formKey++; // refresh Vueform UI state
    },
    hideFooter(val) {
      this.$emit('hideFooter', val);
    },
  },
  watch: {
    cgData: {
      handler() {
        // Recreate validator when cgData changes
        const self = this;
        this.invalidGroup = class extends Validator {
          get msg() {
            return ERROR_GROUP;
          }
          check(value) {
            if (!value) return false;
            if (!self.cgData) return true;
            // Same lookup onRequestSubmit resolves the id with, so the field only
            // passes when a request can actually be sent. Both sides are
            // normalised: a stripped input never equalled a spaced key.
            return resolveCgId(value, self.cgData) !== undefined;
          }
        };
        // Refresh form to pick up new validator
        this.$nextTick(() => {
          this.formKey++;
        });
      },
      immediate: true,
    },
    'formData.cgName'(newVal) {
      // Update formErrors based on validation
      if (this.$refs.formRef) {
        const errors = this.$refs.formRef.el$('cgName')?.errors || [];
        this.formErrors.cgName = errors.length > 0;
        this.disabledSubmit = errors.length > 0;
      }
    },
    payload(newVal) {
      this.updatedPayload = JSON.parse(JSON.stringify(newVal));
      this.formData.cgName = newVal.cgName || '';
      if (newVal.cgUpdateRequestFormId) {
        this.cardTitle = 'Thanks! We’ve received your request and will be in touch soon ✅';
      } else {
        this.cardTitle = 'Request for an update if these fields are incorrect.';
      }
      // Ensure Vueform picks up new values
      this.$nextTick(() => {
        this.formKey++;
      });
    },
    formErrors: {
      deep: true,
      handler(newVal) {
        this.disabledSubmit = newVal.cgName;
      },
    },
  },
};
</script>
<style scoped>
:deep(
  .locked-field input:disabled,
  .locked-field textarea:disabled,
  .locked-field select:disabled
) {
  background-color: #ecebeb !important;
  color: #7c7b78 !important;
  border-color: #ecebeb !important;
  -webkit-text-fill-color: #7c7b78; /* Chrome/Safari */
  opacity: 1; /* undo browser dimming */
  cursor: not-allowed;
}
</style>
